// 사용자 상세(관리) 페이지
// - 계정 상태/역할/소속/전화번호 관리 + 근무 상태는 /manager-status 별도 저장
// - 대직자 선택은 동일 조직에서 후보 제공, 권한 가드 적용
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { Roles, getRoleLabel, getUserRoles } from '../auth/rbac'
import { api } from '../lib/api'
import { formatMenuInstant } from '../lib/date'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { useToast } from '../ui/ToastProvider'

const ActionLabels = {
  // Auth
  LOGIN_SUCCESS: '로그인 성공',
  LOGIN_FAILURE: '로그인 실패',
  LOGIN_LOCKED: '로그인 잠금',
  LOGIN_BLOCKED_STATUS: '로그인 차단(계정상태)',

  // Users
  USER_CREATE: '사용자 생성',
  USER_UPDATE: '사용자 정보 변경',
  USER_ACTIVATE: '사용자 활성화',
  USER_DEACTIVATE: '사용자 비활성화',
  USER_PASSWORD_RESET: '비밀번호 초기화(운영자)',

  ADD_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 추가',
  REMOVE_GA_BRANCH_MAPPING: '원수사 담당자↔GA 매핑 제거',

  // legacy
  변경이력: '변경이력 기록',
}

function labelForAction(action) {
  if (!action) return ''
  return ActionLabels[action] || '기타'
}

export default function UserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { show } = useToast()
  const { user: currentUser } = useAuth()
  const currentRoles = useMemo(() => getUserRoles(currentUser), [currentUser])

  // 전화번호 표시 포맷(XXX-XXXX-XXXX)
  const normalizePhoneDigits = useCallback((v) => String(v || '').replace(/[^0-9]/g, ''), [])
  const formatPhoneDisplay = useCallback((v) => {
    const raw = String(v ?? '').trim()
    if (!raw) return ''
    // 마스킹/이미 포맷된 값은 그대로
    if (raw.includes('*')) return raw
    const d = normalizePhoneDigits(raw)
    if (!d) return ''
    if (d.length <= 3) return d
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`
  }, [normalizePhoneDigits])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [userDetail, setUserDetail] = useState(null)
  const [auditLogs, setAuditLogs] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const [availability, setAvailability] = useState('ON_DUTY')
  const [deputyCandidateOptions, setDeputyCandidateOptions] = useState([])
  const [deputyUserId1, setDeputyUserId1] = useState('')
  const [deputyUserId2, setDeputyUserId2] = useState('')
  const [requireDeputy, setRequireDeputy] = useState(false)
  // 원수사 설계매니저 ↔ GA 지점 매핑
  const [gaMappings, setGaMappings] = useState([])
  const [newGaOrgId, setNewGaOrgId] = useState('')
  const [mappingBusy, setMappingBusy] = useState(false)
  const [mappedGaOrgIds, setMappedGaOrgIds] = useState([]) // 해당 원수사 지점에 매핑된 GA 지점 ID 목록
  const [mappedGaOrgs, setMappedGaOrgs] = useState([]) // /orgs/{id}/mappings 응답의 gas 목록(id,name)

  // 폼 상태
  const [form, setForm] = useState({
    name: '',
    email: '',
    roles: [],
    orgId: '',
    status: 'ACTIVE',
    phoneNumber: ''
  })
  const [statusDraft, setStatusDraft] = useState('ACTIVE')
  const [saving, setSaving] = useState(false)
  const [forbidden, setForbidden] = useState(false)
  const [resetBusy, setResetBusy] = useState(false)
  const [operatorWithdrawBusy, setOperatorWithdrawBusy] = useState(false)

  const canEditAccountStatus = useMemo(() => {
    const roles = currentRoles || []
    return roles.includes(Roles.OPERATOR)
      || roles.includes(Roles.GA_LEADER)
      || roles.includes(Roles.GA_MANAGER)
      || roles.includes(Roles.INSURER_MASTER_ADMIN)
      || roles.includes(Roles.INSURER_MASTER_DEPUTY_ADMIN)
      || roles.includes(Roles.INSURER_LEADER)
      || roles.includes(Roles.INSURER_DEPUTY_LEADER)
      || roles.includes(Roles.INSURER_BRANCH_MANAGER)
      || roles.includes(Roles.INSURER_DEPUTY_BRANCH_MANAGER)
      || roles.includes(Roles.INSURER_OTHER_ADMIN)
  }, [currentRoles])

  const isOperator = useMemo(() => {
    const roles = currentRoles || []
    return roles.includes(Roles.OPERATOR)
  }, [currentRoles])

  const operatorWithdrawTargetSelf = useMemo(() => {
    if (currentUser?.id == null || id == null) return false
    return Number(id) === Number(currentUser.id)
  }, [currentUser?.id, id])

  const targetAccountAlreadyWithdrawn = useMemo(() => {
    const em = String(form?.email || userDetail?.email || '').trim().toLowerCase()
    return em.startsWith('purged.')
  }, [form?.email, userDetail?.email])

  const showOperatorWithdrawPanel = isOperator && !operatorWithdrawTargetSelf && !targetAccountAlreadyWithdrawn

  const canEditCoreFields = canEditAccountStatus // 필요시 세분화 가능

  const isTargetGaPlanner = useMemo(() => {
    const r = form?.roles || userDetail?.roles || []
    return Array.isArray(r) && r.includes(Roles.GA_PLANNER)
  }, [form?.roles, userDetail?.roles])

  const canResetPassword = useMemo(() => {
    const roles = currentRoles || []
    if (roles.includes(Roles.OPERATOR)) return true
    if (roles.includes(Roles.GA_LEADER)) return true
    if (roles.includes(Roles.INSURER_LEADER)) return true
    // 요구사항: GA 관리자는 "본인 소속 하위(=GA 설계사)"만 초기화 가능
    if (roles.includes(Roles.GA_MANAGER)) {
      return Boolean(isTargetGaPlanner)
    }
    return false
  }, [currentRoles, isTargetGaPlanner])

  const targetIsInactive = useMemo(() => {
    const s = userDetail?.status || form?.status
    return s === 'INACTIVE' || s === 'SUSPENDED'
  }, [userDetail?.status, form?.status])

  const canEditWhenActive = useMemo(() => {
    return canEditCoreFields && !targetIsInactive
  }, [canEditCoreFields, targetIsInactive])

  const canEditGaMappings = useMemo(() => {
    const roles = currentRoles || []
    return roles.includes(Roles.OPERATOR)
      || roles.includes(Roles.INSURER_LEADER)
      || roles.includes(Roles.INSURER_BRANCH_MANAGER)
  }, [currentRoles])
  const isTargetInsurerSide = useMemo(() => {
    const r = userDetail?.roles || []
    return r.some(code => String(code).startsWith('INSURER_'))
  }, [userDetail])

  // 역할 선택 옵션은 BE 정책(/users/{id}/allowed-roles) 기준으로 동기화
  const [allowedRoleCodes, setAllowedRoleCodes] = useState([])
  useEffect(() => {
    let ignore = false
    async function loadAllowedRoles() {
      try {
        if (!id) return
        const effectiveOrgId = form?.orgId || userDetail?.orgId
        const qs = effectiveOrgId ? `?orgId=${Number(effectiveOrgId)}` : ''
        const res = await api.get(`/users/${id}/allowed-roles${qs}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const roles = payload?.roles || []
        if (!ignore) setAllowedRoleCodes(Array.isArray(roles) ? roles : [])
      } catch {
        if (!ignore) setAllowedRoleCodes([])
      }
    }
    loadAllowedRoles()
    return () => { ignore = true }
  }, [id, form?.orgId, userDetail?.orgId])

  const effectiveOrgId = useMemo(() => {
    const v = form?.orgId || userDetail?.orgId
    return v ? Number(v) : null
  }, [form?.orgId, userDetail?.orgId])

  const targetOrgType = useMemo(() => {
    // orgOptions에서 조직 타입(GA/INSURER)을 우선 파악, 없으면 role prefix로 폴백
    const o = (effectiveOrgId != null)
      ? (Array.isArray(orgOptions) ? orgOptions.find(x => Number(x?.id) === Number(effectiveOrgId)) : null)
      : null
    const t = o?.type
    if (t === 'GA' || t === 'INSURER') return t
    const r = userDetail?.roles || form?.roles || []
    const arr = Array.isArray(r) ? r.map(String) : []
    if (arr.some(code => code.startsWith('INSURER_'))) return 'INSURER'
    if (arr.some(code => code.startsWith('GA_'))) return 'GA'
    return null
  }, [effectiveOrgId, orgOptions, userDetail?.roles, form?.roles])

  // 대표 역할은 운영자만 선택 가능(요구사항)
  // - 비운영자 화면에서는 아예 리스트에서 숨기되, 이미 대표 역할이 붙어있으면 저장 시에도 보존해야 한다.
  const immutableRolesToPreserve = useMemo(() => {
    const out = new Set()
    const current = Array.isArray(form?.roles) ? form.roles.map(String) : []
    const original = Array.isArray(userDetail?.roles) ? userDetail.roles.map(String) : []
    const all = Array.from(new Set([...current, ...original]))

    // 1) 대표 역할: 운영자가 아니면 항상 immutable로 취급(유지/숨김)
    if (!isOperator) {
      if (all.includes(Roles.GA_LEADER)) out.add(Roles.GA_LEADER)
      if (all.includes(Roles.INSURER_LEADER)) out.add(Roles.INSURER_LEADER)
    }

    return Array.from(out)
  }, [form?.roles, userDetail?.roles, isOperator])

  const disallowedSelectedRoles = useMemo(() => {
    const allowed = new Set((allowedRoleCodes || []).map(String))
    const immutable = new Set((immutableRolesToPreserve || []).map(String))
    return (form?.roles || []).filter(r => !allowed.has(String(r)) && !immutable.has(String(r)))
  }, [allowedRoleCodes, form?.roles, immutableRolesToPreserve])

  const visibleRoleOptionCodes = useMemo(() => {
    // 정책: 사용자 상세의 "선택 가능한 역할"은 실사용 역할(3+4)만 노출한다.
    // - GA: (운영자) 대표/관리자/설계사, (비운영자) 관리자/설계사
    // - INSURER: (운영자) 대표/관리자/매니저/설계사, (비운영자) 관리자/매니저/설계사
    const immutable = new Set((immutableRolesToPreserve || []).map(String))
    const compact =
      targetOrgType === 'GA'
        ? [
          ...(isOperator ? [Roles.GA_LEADER] : []),
          Roles.GA_MANAGER,
          Roles.GA_OPERATION_MANAGER,
          Roles.GA_PLANNER,
        ]
        : targetOrgType === 'INSURER'
          ? [
            ...(isOperator ? [Roles.INSURER_LEADER] : []),
            Roles.INSURER_MANAGER,
            Roles.INSURER_OPERATION_MANAGER,
            Roles.INSURER_PLANNER,
          ]
          : []
    return compact
      .map(String)
      .filter(code => !immutable.has(String(code)))
      .sort()
  }, [immutableRolesToPreserve, isOperator, targetOrgType])

  // 사용자 상세 정보 로딩
  useEffect(() => {
    let ignore = false
    async function loadUserDetail() {
      setLoading(true)
      setError(null)
      try {
        const [userRes, auditRes] = await Promise.all([
          api.get(`/users/${id}`),
          api.get(`/users/${id}/audit-logs`)
        ])

        if (!ignore) {
          const user = (userRes?.data && userRes.data.data !== undefined) ? userRes.data.data : userRes.data || {}
          setUserDetail(user)
          setForm({
            name: user.name || '',
            email: user.email || '',
            roles: user.roles || [],
            orgId: user.orgId || '',
            status: user.status || 'ACTIVE', // 계정 상태
            phoneNumber: formatPhoneDisplay(user.phoneNumber || '')
          })
          setStatusDraft(user.status || 'ACTIVE')
          setAvailability(user.availabilityStatus || 'ON_DUTY')
          // 대직자 로드: 신규(deputyUserIds) 우선, 없으면 하위호환(deputyUserId) 사용
          const depIds = Array.isArray(user.deputyUserIds) ? user.deputyUserIds : []
          if (depIds.length > 0) {
            setDeputyUserId1(depIds[0] != null ? String(depIds[0]) : '')
            setDeputyUserId2(depIds[1] != null ? String(depIds[1]) : '')
          } else {
            // 하위호환: 서버에서 내려주는 deputyUserId는 "1순위"만 의미
            if (user.deputyUserId) setDeputyUserId1(String(user.deputyUserId))
            else setDeputyUserId1('')
            setDeputyUserId2('')
          }
          const logs = (auditRes?.data && auditRes.data.data !== undefined) ? auditRes.data.data : auditRes.data
          setAuditLogs(Array.isArray(logs) ? logs : [])
        }
      } catch (e) {
        if (!ignore) setError('사용자 정보를 불러오지 못했습니다')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    loadUserDetail()
    return () => { ignore = true }
  }, [formatPhoneDisplay, id])

  // 조직 옵션 로딩
  useEffect(() => {
    let ignore = false
    async function loadOrgOptions() {
      try {
        const scope = isOperator ? 'all' : 'mine'
        const res = await api.get('/orgs/options', { params: { scope } })
        if (!ignore) {
          const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
          const all = Array.isArray(payload) ? payload : []
          // 모든 조직(INSURER/GA) 노출: 상세 사용자는 양쪽 모두 가능
          setOrgOptions(all)
        }
      } catch (e) {
        console.error('Failed to load org options:', e)
      }
    }
    loadOrgOptions()
    return () => { ignore = true }
  }, [isOperator])

  // GA 지점 매핑 로딩
  useEffect(() => {
    let ignore = false
    async function loadMappings() {
      try {
        const res = await api.get(`/users/${id}/ga-branches`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setGaMappings(Array.isArray(payload) ? payload : [])
      } catch {
        if (!ignore) setGaMappings([])
      }
    }
    // 원수사측 사용자만 조회
    if (isTargetInsurerSide) loadMappings()
    return () => { ignore = true }
  }, [id, isTargetInsurerSide])

  // 조직 ID → 조직 객체 맵
  const orgById = useMemo(() => {
    const map = new Map()
    for (const o of orgOptions) {
      map.set(o.id, o)
    }
    return map
  }, [orgOptions])

  // 원수사 지점에 매핑된 GA 지점 목록 로딩 (조직-조직상세 매핑 기준)
  useEffect(() => {
    let ignore = false
    async function loadInsurerOrgGaMappings() {
      try {
        if (!isTargetInsurerSide || !userDetail?.orgId) {
          if (!ignore) setMappedGaOrgIds([])
          if (!ignore) setMappedGaOrgs([])
          return
        }
        // 원수사 조직 ↔ GA 운영단위 매핑은 /orgs/{id}/mappings 로 조회한다.
        const res = await api.get(`/orgs/${userDetail.orgId}/mappings`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const gas = payload?.gas || []
        if (!ignore) {
          const list = Array.isArray(gas) ? gas : []
          setMappedGaOrgs(list)
          setMappedGaOrgIds(list.map(g => g.id))
        }
      } catch {
        if (!ignore) {
          setMappedGaOrgIds([])
          setMappedGaOrgs([])
        }
      }
    }
    loadInsurerOrgGaMappings()
    return () => { ignore = true }
  }, [isTargetInsurerSide, userDetail?.orgId])

  // GA 지점 경로(구분 / 1차 / 2차)를 계산
  const getGaPath = useCallback((orgId, fallbackName) => {
    const start = orgById.get(orgId)
    if (!start) return fallbackName || String(orgId)

    // `/orgs/options` 에서 path 를 내려주면 우선 사용
    if (start.path) return start.path

    const segments = []
    let current = start
    let safety = 0
    while (current && safety < 5 && segments.length < 3) {
      segments.unshift(current.name)
      current = current.parentOrgId != null ? orgById.get(current.parentOrgId) : null
      safety += 1
    }
    return segments.join(' / ')
  }, [orgById])

  // 선택 가능한 GA 지점 목록: GA 타입 + 해당 원수사 지점에 매핑된 GA만
  const gaBranchSelectOptions = useMemo(() => {
    if (!Array.isArray(mappedGaOrgs) || mappedGaOrgs.length === 0) return []
    // NOTE:
    // - 원수사 관리자는 /orgs/options?scope=mine 으로 INSURER 트리만 내려오는 경우가 많아
    //   orgOptions에서 GA org를 찾을 수 없다.
    // - 따라서 /orgs/{id}/mappings 의 gas(id,name) 자체로 옵션을 구성하되,
    //   orgOptions에 해당 GA가 존재하면 path(구분/본부/지점)까지 보강한다.
    return mappedGaOrgs
      .filter(g => g && g.id != null)
      .map(g => {
        const id = g.id
        const inOptions = orgById.get(id)
        const label = inOptions?.path
          ? inOptions.path
          : getGaPath(id, g.name || inOptions?.name || `조직 ${id}`)
        return { id, label }
      })
  }, [mappedGaOrgs, orgById, getGaPath])

  const unmappedUserGaMappings = useMemo(() => {
    // 사용자에 매핑은 되어 있으나, 현재 "조직↔조직 매핑"에는 포함되지 않는 케이스(과거 데이터/운영 실수)를 탐지
    const orgMapped = new Set((mappedGaOrgIds || []).map(x => String(x)))
    return (gaMappings || []).filter(m => m?.gaBranchOrgId != null && !orgMapped.has(String(m.gaBranchOrgId)))
  }, [gaMappings, mappedGaOrgIds])

  const addGaMapping = async () => {
    if (!newGaOrgId) return show('GA 지점을 선택해주세요', 'error')
    setMappingBusy(true)
    try {
      await api.post(`/users/${id}/ga-branches`, { gaBranchOrgId: Number(newGaOrgId) })
      setNewGaOrgId('')
      const res = await api.get(`/users/${id}/ga-branches`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setGaMappings(Array.isArray(payload) ? payload : [])
      show('매핑이 추가되었습니다', 'success')
    } catch {
      show('매핑 추가 실패', 'error')
    } finally {
      setMappingBusy(false)
    }
  }

  const removeGaMapping = async (gaBranchOrgId) => {
    if (!window.confirm('해당 매핑을 삭제하시겠습니까?')) return
    setMappingBusy(true)
    try {
      await api.delete(`/users/${id}/ga-branches/${gaBranchOrgId}`)
      setGaMappings(prev => prev.filter(m => String(m.gaBranchOrgId) !== String(gaBranchOrgId)))
      show('매핑이 삭제되었습니다', 'success')
    } catch {
      show('매핑 삭제 실패', 'error')
    } finally {
      setMappingBusy(false)
    }
  }

  const runOperatorWithdrawal = async () => {
    if (!isOperator || operatorWithdrawTargetSelf || targetAccountAlreadyWithdrawn) return
    const label = form.name || form.email || userDetail?.name || userDetail?.email || `#${id}`
    const ok = window.confirm(
      `「${label}」계정을 탈퇴(비식별화) 처리합니다. 로그인할 수 없게 되며 되돌리기 어렵습니다. 계속할까요?`,
    )
    if (!ok) return
    setOperatorWithdrawBusy(true)
    try {
      await api.post(`/users/${id}/withdrawal`, {})
      show('탈퇴 처리되었습니다.', 'success')
      navigate('/users')
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '처리에 실패했습니다.'
      show(msg, 'error')
    } finally {
      setOperatorWithdrawBusy(false)
    }
  }

  const resetPassword = async () => {
    if (!canResetPassword) return
    if (!window.confirm('임시 비밀번호로 초기화할까요? (사용자에게 전달 필요)')) return
    setResetBusy(true)
    try {
      const res = await api.post(`/users/${id}/reset-password`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const temp = payload?.tempPassword
      if (temp) {
        window.prompt('임시 비밀번호(복사해서 전달하세요)', temp)
      }
      show('비밀번호가 초기화되었습니다', 'success')
    } catch {
      show('비밀번호 초기화 실패', 'error')
    } finally {
      setResetBusy(false)
    }
  }

  // 역할 토글
  const toggleRole = useCallback((role) => {
    setForm(prev => {
      // 대표(및 보존 대상) 역할은 변경 불가
      if ((immutableRolesToPreserve || []).includes(role)) return prev
      const has = prev.roles.includes(role)
      if (has) {
        return { ...prev, roles: prev.roles.filter(r => r !== role) }
      }
      if (!allowedRoleCodes.includes(role)) return prev
      return { ...prev, roles: [...prev.roles, role] }
    })
  }, [allowedRoleCodes, immutableRolesToPreserve])

  const normalizeRolesToAllowed = useCallback(() => {
    setForm(prev => ({
      ...prev,
      roles: Array.from(new Set([
        ...((immutableRolesToPreserve || []).map(String)),
        ...((prev.roles || []).filter(r => allowedRoleCodes.includes(r)).map(String)),
      ]))
    }))
  }, [allowedRoleCodes, immutableRolesToPreserve])

  // 사용자 정보 저장
  const onSave = async (e) => {
    e.preventDefault()
    if (disallowedSelectedRoles.length > 0) {
      show('선택한 조직 기준으로 허용되지 않는 역할이 포함되어 있습니다. 역할을 정리 후 저장해주세요.', 'error')
      return
    }
    setSaving(true)
    try {
      const needsDeputy = availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING'
      const deputyIds = [deputyUserId1, deputyUserId2].filter(Boolean).map(String)
      const uniqueDeputyIds = Array.from(new Set(deputyIds))
      if (uniqueDeputyIds.length !== deputyIds.length) {
        show('대직자 1순위/2순위가 중복되었습니다. 다시 선택해주세요.', 'error')
        setSaving(false)
        return
      }
      if (needsDeputy && uniqueDeputyIds.length > 0) {
        const ok = uniqueDeputyIds.every(did => deputyCandidateOptions.some(o => String(o.id) === String(did)))
        if (!ok) {
          show('대직자는 선택된 소속 조직 기준 후보 목록에서만 선택할 수 있습니다. 대직자를 다시 선택해주세요.', 'error')
          setSaving(false)
          return
        }
      }
      if (needsDeputy && uniqueDeputyIds.length === 0) {
        show('휴가/교육/부재중일 때는 대직자 설정이 필요합니다.', 'error')
        setSaving(false)
        return
      }

      // 1) 사용자 기본 정보/권한/조직/계정상태 저장
      try {
        await api.patch(`/users/${id}`, {
          name: form.name,
          roles: form.roles,
          orgId: form.orgId ? Number(form.orgId) : undefined,
          status: form.status,
          // 빈 문자열도 저장(전화번호 초기화/수정 모두 반영)
          phoneNumber: form.phoneNumber
        })
      } catch (e) {
        if (e?.response?.status === 403) {
          setForbidden(true)
        }
        throw e
      }

      // 2) 근무상태는 별도 API로 저장 (/manager-status)
      try {
        const deputyUserIds = uniqueDeputyIds.map(n => Number(n))
        await api.post('/manager-status', {
          userId: Number(id),
          status: availability,
          startAt: new Date().toISOString(),
          endAt: null,
          deputyUserId: deputyUserIds.length > 0 ? deputyUserIds[0] : null, // 하위호환
          deputyUserIds,
        })
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message
        show(`근무 상태/대직자 저장 실패${msg ? ` (${msg})` : ''}`, 'error')
        throw e
      }

      show('저장 완료', 'success')

      // 데이터 새로고침
      const [userRes, auditRes] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/users/${id}/audit-logs`)
      ])
      const user = (userRes?.data && userRes.data.data !== undefined) ? userRes.data.data : userRes.data || {}
      setUserDetail(user)
      setAvailability(user.availabilityStatus || 'ON_DUTY')
      const depIds = Array.isArray(user.deputyUserIds) ? user.deputyUserIds : []
      if (depIds.length > 0) {
        setDeputyUserId1(depIds[0] != null ? String(depIds[0]) : '')
        setDeputyUserId2(depIds[1] != null ? String(depIds[1]) : '')
      } else {
        if (user.deputyUserId) setDeputyUserId1(String(user.deputyUserId))
        else setDeputyUserId1('')
        setDeputyUserId2('')
      }
      const logs = (auditRes?.data && auditRes.data.data !== undefined) ? auditRes.data.data : auditRes.data
      setAuditLogs(Array.isArray(logs) ? logs : [])
    } catch (err) {
      show('저장 실패', 'error')
    } finally {
      setSaving(false)
    }
  }

  // 대직자 후보 로딩 (동일 조직)
  useEffect(() => {
    let ignore = false
    async function loadDeputyCandidates() {
      try {
        if (!effectiveOrgId) {
          if (!ignore) setDeputyCandidateOptions([])
          return
        }
        // "선택된 소속 조직" 기준 후보 제공 (조직 변경 시에도 UX/저장 기준 일치)
        const res = await api.get('/users', { params: { orgId: effectiveOrgId, size: 1000 } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const raw = Array.isArray(payload) ? payload : (payload?.content || payload?.items || [])
        const mapped = raw.filter(u => String(u.id) !== String(id)).map(u => ({ id: u.id, label: `${u.name || u.email} (${u.email})` }))
        if (!ignore) setDeputyCandidateOptions(mapped)
      } catch {
        if (!ignore) setDeputyCandidateOptions([])
      }
    }
    loadDeputyCandidates()
    return () => { ignore = true }
  }, [effectiveOrgId, id])

  // 조직 변경 시: 기존 대직자가 새 조직 후보에 없으면 자동 해제(저장 실패/혼란 방지)
  useEffect(() => {
    // 후보 목록이 아직 로딩되지 않았을 때(deputyCandidateOptions = [])는
    // "후보에 없음"으로 오판하여 대직자를 지우는 레이스가 발생할 수 있다.
    // - 후보 로딩 이후에만 검증/자동해제를 수행한다.
    const isOk = (val) => {
      if (!val) return true
      if (!Array.isArray(deputyCandidateOptions) || deputyCandidateOptions.length === 0) return true
      return deputyCandidateOptions.some(o => String(o.id) === String(val))
    }
    if (!isOk(deputyUserId1)) setDeputyUserId1('')
    if (!isOk(deputyUserId2)) setDeputyUserId2('')
    if (deputyUserId1 && deputyUserId2 && String(deputyUserId1) === String(deputyUserId2)) {
      setDeputyUserId2('')
    }
  }, [deputyCandidateOptions, deputyUserId1, deputyUserId2])

  // 근무상태 변경 시 대직자 필수 여부 및 자동 설정
  useEffect(() => {
    const needsDeputy = (availability === 'VACATION' || availability === 'AWAY' || availability === 'TRAINING') && !isTargetGaPlanner
    setRequireDeputy(needsDeputy)
    if (needsDeputy && !deputyUserId1 && deputyCandidateOptions.length > 0) {
      // 자동 지정: 1순위만 기본으로 선택
      setDeputyUserId1(String(deputyCandidateOptions[0].id))
    }
  }, [availability, deputyUserId1, deputyCandidateOptions, isTargetGaPlanner])

  const onActivate = async () => {
    if (!window.confirm('이 계정을 다시 활성화하시겠습니까?')) return
    setSaving(true)
    try {
      await api.patch(`/users/${id}/activate`)
      show('활성화 완료', 'success')
      const [userRes, auditRes] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/users/${id}/audit-logs`)
      ])
      const user = (userRes?.data && userRes.data.data !== undefined) ? userRes.data.data : userRes.data || {}
      const logs = (auditRes?.data && auditRes.data.data !== undefined) ? auditRes.data.data : auditRes.data
      setUserDetail(user)
      setAuditLogs(Array.isArray(logs) ? logs : [])
      setForm(prev => ({ ...prev, status: user.status || prev.status }))
      setStatusDraft(user.status || 'ACTIVE')
    } catch (err) {
      show('활성화 실패', 'error')
    } finally {
      setSaving(false)
    }
  }

  const onSaveAccountStatus = async () => {
    if (!canEditAccountStatus) return
    const next = statusDraft || 'ACTIVE'
    if (!window.confirm(`계정 상태를 "${next}"(으)로 변경하시겠습니까?`)) return

    setSaving(true)
    try {
      await api.patch(`/users/${id}`, { status: next })
      show('계정 상태 저장 완료', 'success')
      const [userRes, auditRes] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/users/${id}/audit-logs`)
      ])
      const user = (userRes?.data && userRes.data.data !== undefined) ? userRes.data.data : userRes.data || {}
      const logs = (auditRes?.data && auditRes.data.data !== undefined) ? auditRes.data.data : auditRes.data
      setUserDetail(user)
      setAuditLogs(Array.isArray(logs) ? logs : [])
      setForm(prev => ({ ...prev, status: user.status || prev.status }))
      setStatusDraft(user.status || next)
    } catch (err) {
      const msg = err?.response?.data?.message || '계정 상태 저장 실패'
      show(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <Loading />
  if (error) return <InlineError message={error} />
  if (!userDetail) return <div className="text-sm text-slate-600">사용자 정보를 찾을 수 없습니다</div>

  return (
    <section>
      <h1 className="text-2xl font-bold">사용자 상세 #{id}</h1>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 사용자 정보 수정 */}
        <div className="rounded border p-4">
          <h2 className="font-semibold mb-2">사용자 정보</h2>
          {userDetail && (
            <div className="mb-2 text-xs text-slate-600">
              {(() => {
                const isOp = isOperator
                const root = userDetail.orgRootName || '-'
                const parent = userDetail.orgParentName || '-'
                const org = userDetail.orgName || '-'
                const parts = []
                if (isOp) {
                  parts.push(root, parent, org)
                } else {
                  if (parent !== '-' && parent !== root) parts.push(parent)
                  parts.push(org)
                }
                return (
                  <>
                    현재 소속: <span className="font-medium">{parts.filter(p => p && p !== '-').join(' | ') || '-'}</span>
                  </>
                )
              })()}
            </div>
          )}
          <form onSubmit={onSave} className="space-y-4 text-sm">
            <div>
              <label className="block text-slate-600 mb-1">이름</label>
              <input className="w-full rounded border px-3 py-2" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} disabled={!canEditWhenActive} />
            </div>

            <div>
              <label className="block text-slate-600 mb-1">이메일</label>
              <input className="w-full rounded border px-3 py-2 bg-slate-50" value={form.email} disabled />
            </div>

            <div>
              <label className="block text-slate-600 mb-2">역할 (다중 선택)</label>
              <div className="text-xs text-slate-500 mb-2">
                선택된 소속 조직 기준으로 허용 역할이 자동 갱신됩니다. (허용 목록 밖 역할은 저장 시 거부됨)
              </div>
              {canEditCoreFields && disallowedSelectedRoles.length > 0 && (
                <div className="mb-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 flex items-center justify-between gap-2">
                  <div>
                    현재 선택된 조직 기준으로 허용되지 않는 역할이 선택되어 있습니다:
                    <span className="font-medium"> {disallowedSelectedRoles.map(getRoleLabel).join(', ')}</span>
                  </div>
                  <button type="button" className="shrink-0 rounded border border-rose-200 bg-white px-2 py-1 hover:bg-rose-50" onClick={normalizeRolesToAllowed}>
                    허용 역할로 정리
                  </button>
                </div>
              )}
              <div className="space-y-2">
                {visibleRoleOptionCodes.map(role => (
                  <label key={role} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={form.roles.includes(role)}
                      onChange={() => toggleRole(role)}
                      disabled={!canEditWhenActive || (!allowedRoleCodes.includes(role) && !form.roles.includes(role))}
                      className="rounded"
                    />
                    <span className="text-sm">{getRoleLabel(role)}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-slate-600 mb-1">소속 조직</label>
              <select className="w-full rounded border px-3 py-2" value={form.orgId} onChange={(e) => setForm(prev => ({ ...prev, orgId: e.target.value }))} disabled={!canEditWhenActive}>
                <option value="">조직 선택</option>
                {orgOptions.map(org => (
                  <option key={org.id} value={org.id}>{org.name} ({org.type})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-600 mb-1">전화번호</label>
              <input
                className="w-full rounded border px-3 py-2"
                value={form.phoneNumber}
                onChange={(e) => setForm(prev => ({ ...prev, phoneNumber: formatPhoneDisplay(e.target.value) }))}
                placeholder="010-0000-0000"
                disabled={!canEditWhenActive}
              />
            </div>


            <div>
              <label className="block text-slate-600 mb-1">근무 상태</label>
              <select className="w-full rounded border px-3 py-2" value={availability} onChange={(e) => setAvailability(e.target.value)} disabled={!canEditWhenActive}>
                <option value="ON_DUTY">근무중</option>
                <option value="VACATION">휴가</option>
                <option value="TRAINING">교육</option>
                <option value="AWAY">부재중</option>
              </select>
              {!isTargetGaPlanner && (
                <div className="mt-2">
                  <label className="block text-slate-600 mb-1">대직자 1순위(동일 조직)</label>
                  <select className={`w-full rounded border px-3 py-2 ${requireDeputy && !deputyUserId1 ? 'border-red-300' : ''}`} value={deputyUserId1} onChange={(e) => setDeputyUserId1(e.target.value)} required={requireDeputy} disabled={!canEditWhenActive}>
                    <option value="">선택</option>
                    {deputyCandidateOptions.map(opt => (<option key={opt.id} value={String(opt.id)}>{opt.label}</option>))}
                  </select>
                  {requireDeputy && !deputyUserId1 && (<p className="text-xs text-red-600 mt-1">휴가/교육/부재중 시 대직자 1순위 설정이 필수입니다.</p>)}
                  <div className="mt-2">
                    <label className="block text-slate-600 mb-1">대직자 2순위(선택)</label>
                    <select className="w-full rounded border px-3 py-2" value={deputyUserId2} onChange={(e) => setDeputyUserId2(e.target.value)} disabled={!canEditWhenActive}>
                      <option value="">선택 안 함</option>
                      {deputyCandidateOptions
                        .filter(opt => String(opt.id) !== String(deputyUserId1))
                        .map(opt => (<option key={opt.id} value={String(opt.id)}>{opt.label}</option>))}
                    </select>
                    <div className="text-[11px] text-slate-500 mt-1">1순위가 근무 불가(교육/휴가/부재중)일 때 2순위로 자동 전환됩니다. (근무 가능=ON_DUTY만)</div>
                  </div>
                </div>
              )}
              {isTargetGaPlanner && (
                <div className="mt-2 text-xs text-slate-500">
                  GA 설계사 역할은 설계의뢰 자동 배정 대상이 아니므로 대직자 설정이 제공되지 않습니다.
                </div>
              )}
            </div>

            {/* <div>
              <label className="block text-slate-600 mb-1">계정 상태</label>
              <select className="w-full rounded border px-3 py-2" value={form.status} onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))} disabled={!canEditAccountStatusWhenActive}>
                <option value="ACTIVE">활성</option>
                <option value="INACTIVE">비활성</option>
                <option value="SUSPENDED">정지</option>
              </select>
              {!canEditAccountStatus && <div className="text-xs text-slate-500 mt-1">계정 상태는 관리자만 수정할 수 있습니다.</div>}
              {targetIsInactive && (
                <div className="text-xs text-amber-700 mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                  비활성/정지 계정은 <span className="font-medium">상태 복구(활성화)</span> 외 정보 수정이 불가능합니다.
                </div>
              )}
            </div> */}

            <div className="pt-4 flex items-center gap-2">
              <button type="submit" className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-60" disabled={saving || forbidden || disallowedSelectedRoles.length > 0 || targetIsInactive}>{saving ? '저장 중...' : '저장'}</button>
              {targetIsInactive && canEditAccountStatus && (
                <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60" onClick={onActivate} disabled={saving}>
                  {saving ? '처리 중...' : '활성화'}
                </button>
              )}
              {canResetPassword && (
                <button
                  type="button"
                  className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                  onClick={resetPassword}
                  disabled={resetBusy || targetIsInactive}
                >
                  {resetBusy ? '초기화 중...' : '비밀번호 초기화'}
                </button>
              )}
              {forbidden && (<span className="text-xs text-red-600">권한이 없어 수정할 수 없습니다.</span>)}
              {!forbidden && disallowedSelectedRoles.length > 0 && (
                <span className="text-xs text-rose-700">허용되지 않는 역할을 먼저 정리해주세요.</span>
              )}
            </div>
          </form>
        </div>

        {/* 비활성화 및 감사 로그 */}
        <div className="space-y-4">
          {/* 계정 상태(별도 저장) */}
          <div className="rounded border p-4">
            <h2 className="font-semibold mb-2">계정 상태</h2>
            <div className="text-xs text-slate-600 mb-3">
              계정 상태는 로그인/토큰 재발급에 직접 영향을 줍니다. 꼭 필요한 경우에만 수정하세요.<br/><br/>
              <span className="font-medium">활성상태가 아니면 로그인/리프레시가 차단</span>됩니다.
              <div className="mt-2 text-[11px] text-slate-500 space-y-1">
                <div>- <span className="font-medium">활성(ACTIVE)</span>: 정상 사용</div>
                <div>- <span className="font-medium">비활성(INACTIVE)</span>: 퇴사/계약종료 등으로 계정을 종료(원칙적으로 정보 수정/업무 처리 불가)</div>
                <div>- <span className="font-medium">정지(SUSPENDED)</span>: 정책/운영 이슈 등으로 일시 중지(필요 시 활성으로 복구)</div>
              </div>
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-slate-600 mb-1 text-sm">상태 선택</label>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  disabled={!canEditAccountStatus || saving}
                >
                  <option value="ACTIVE">활성</option>
                  <option value="INACTIVE">비활성</option>
                  <option value="SUSPENDED">정지</option>
                </select>
                {!canEditAccountStatus && (
                  <div className="text-xs text-slate-500 mt-1">계정 상태는 관리자만 수정할 수 있습니다.</div>
                )}
                {targetIsInactive && (
                  <div className="text-xs text-amber-700 mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2">
                    비활성/정지 계정은 <span className="font-medium">상태 복구(활성화)</span> 외 정보 수정이 불가능합니다.
                  </div>
                )}
              </div>
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-4 py-2 disabled:opacity-60"
                onClick={onSaveAccountStatus}
                disabled={!canEditAccountStatus || saving || !statusDraft}
              >
                {saving ? '저장 중...' : '상태 저장'}
              </button>
            </div>
          </div>

          {showOperatorWithdrawPanel && (
            <div className="rounded border border-rose-200 bg-rose-50/60 p-4">
              <h2 className="font-semibold mb-2 text-rose-900">운영자 전용 · 계정 탈퇴(비식별화)</h2>
              <p className="text-xs text-rose-800/90 mb-3 leading-relaxed">
                로그인 불가 등으로 본인 탈퇴가 어려운 경우에만 사용하세요. 처리 시 이메일·이름·소속 등이 비식별화되고
                톰스톤 조직으로 이동하며, 소셜 연동·토큰 등이 정리됩니다. 사용자 목록에서 실수로 누르는 일을 막기 위해
                이 화면에서만 실행할 수 있습니다.
              </p>
              <button
                type="button"
                className="rounded bg-rose-700 text-white px-4 py-2 text-sm hover:bg-rose-800 disabled:opacity-50"
                onClick={() => void runOperatorWithdrawal()}
                disabled={operatorWithdrawBusy}
              >
                {operatorWithdrawBusy ? '처리 중…' : '탈퇴(비식별화) 실행'}
              </button>
            </div>
          )}

          {/* 원수사 설계매니저 ↔ GA 지점 매핑 */}
          {isTargetInsurerSide && (
            <div className="rounded border p-4">
              <h2 className="font-semibold mb-4">관리 GA 지점 매핑</h2>
              <div className="space-y-3 text-sm">
                <div className="rounded bg-slate-50 border px-3 py-2 text-xs text-slate-600">
                  이 매핑은 <span className="font-medium">의뢰 자동배정/후보 매니저</span>에 직접 영향을 줍니다.
                  <br />
                  선택 가능한 GA 목록은 <span className="font-medium">조직 관리의 “원수사 지점 ↔ GA 운영단위 매핑”</span>에 등록된 항목만 표시됩니다.
                </div>
                {canEditGaMappings && gaBranchSelectOptions.length === 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    현재 이 원수사 조직에 매핑된 GA 운영단위가 없습니다. 먼저 <span className="font-medium">조직 관리</span>에서 원수사 지점 ↔ GA 운영단위 매핑을 추가하세요.
                  </div>
                )}
                {unmappedUserGaMappings.length > 0 && (
                  <div className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    주의: 사용자에 등록된 GA 매핑 중 일부가 현재 조직 매핑(원수사↔GA)에 포함되지 않습니다. 필요 시 삭제 후 다시 설정해주세요.
                  </div>
                )}
                {/* 목록 */}
                <div className="space-y-2">
                  {gaMappings.length === 0 ? (
                    <div className="text-slate-500">등록된 매핑이 없습니다</div>
                  ) : (
                    gaMappings.map(m => (
                      <div key={m.gaBranchOrgId} className="flex items-center justify-between border rounded px-3 py-2">
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            <span>{getGaPath(m.gaBranchOrgId, m.gaBranchOrgName)}</span>
                            {Array.isArray(mappedGaOrgIds) && mappedGaOrgIds.map(x => String(x)).includes(String(m.gaBranchOrgId)) ? (
                              <span className="text-[10px] px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700">조직매핑 OK</span>
                            ) : (
                              <span className="text-[10px] px-2 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700">불일치</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">#{m.gaBranchOrgId} · {m.createdAt}</div>
                        </div>
                        {canEditGaMappings && (
                          <button type="button" className="text-red-600 text-xs border border-red-200 rounded px-2 py-1 hover:bg-red-50 disabled:opacity-60" onClick={() => removeGaMapping(m.gaBranchOrgId)} disabled={mappingBusy}>삭제</button>
                        )}
                      </div>
                    ))
                  )}
                </div>
                {/* 추가 */}
                {canEditGaMappings && (
                  <div className="flex items-center gap-2">
                    <select className="rounded border px-3 py-2 flex-1" value={newGaOrgId} onChange={(e) => setNewGaOrgId(e.target.value)}>
                      <option value="">GA 지점 선택</option>
                      {gaBranchSelectOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                    <button type="button" className="rounded border border-teal-200 bg-teal-50 px-3 py-2 font-semibold text-teal-800 hover:bg-teal-100 disabled:opacity-60" onClick={addGaMapping} disabled={mappingBusy || !newGaOrgId}>{mappingBusy ? '추가 중...' : '추가'}</button>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* 감사 로그 */}
          <div className="rounded border p-4">
            <h2 className="font-semibold mb-4">변경 이력</h2>
            <div className="space-y-2 text-sm max-h-80 overflow-auto">
              {auditLogs.length === 0 ? (
                <div className="text-slate-500">변경 이력없습니다</div>
              ) : (
                auditLogs.map((log, index) => (
                  <div key={index} className="border-l-2 border-slate-200 pl-3 py-1">
                    <div className="font-medium">{labelForAction(log.action)}</div>
                    <div className="text-slate-600 text-xs">
                      시간 : {log.createdAt || log.createdAtDisplay ? formatMenuInstant(log, 'createdAt') : (log.timestamp || '')} |  
                      변경 : {log.actor ? ` · ${log.actor}` : (log.performedBy ? ` · ${log.performedBy}` : '')}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {/* {log.action ? `코드: ${log.action}` : ''}
                      {log.resource ? ` · 대상: ${log.resource}` : ''} */}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          {/* 비활성화 */}
          {/* {(!targetIsInactive) && (
            <div className="rounded border p-4">
              <h2 className="font-semibold mb-4 text-red-600">비활성화 설정(중요)</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <label className="block text-slate-600 mb-1">비활성화 사유</label>
                  <textarea className="w-full rounded border px-3 py-2" rows={3} value={deactivateReason} onChange={(e) => setDeactivateReason(e.target.value)} placeholder="비활성화 사유를 입력해주세요" />
                </div>
                <button type="button" className="rounded border border-red-300 text-red-600 px-3 py-2 hover:bg-red-50" onClick={onDeactivate} disabled={saving || !deactivateReason.trim()}>
                  {saving ? '처리 중...' : '비활성화'}
                </button>
              </div>
            </div>
          )} */}

          
          
        </div>
      </div>
    </section>
  )
}


